import React, { Ref, useEffect, useEffectEvent, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import type { AbsoluteViewportRestriction } from "../../mapiq-map/AbsoluteViewportRestriction";
import type { MarkerData } from "../../mapiq-map/markers/NodeMarkerData";
import type { SetStyleConfig } from "../../mapiq-map/palette/types";
import type { LocationFeatureCollection } from "../../types/MpqGeoJSON";
import type { MapiqMap } from "../../mapiq-map/MapiqMap";
import type { MapState } from "../../mapiq-map/MapState";
import type { EventTarget } from "../../mapiq-map/EventTarget";

type Map2d = string;
type Map3d = LocationFeatureCollection;

type MapData2d = {
  type: "2d";
  map: Map2d;
};

type MapData3d = {
  type: "3d";
  map: Map3d;
};

export type MapData = MapData2d | MapData3d;

type EventHandler = (target: EventTarget) => void;

export type CreateMapiqMap = (options: {
  mapData: MapData;
  viewportRestriction: AbsoluteViewportRestriction;
  disablePointerEvents: boolean;
  container: HTMLElement;
}) => MapiqMap;

export type MapViewProps = {
  mapData: MapData;
  interactive?: boolean;

  // We need to figure out how to dynamically load this
  resolveMapiqMap: CreateMapiqMap;

  // Default zoom
  defaultNodeIdsInView?: Array<string>;
  defaultViewportRestriction?: AbsoluteViewportRestriction;

  mapState?: MapState;
  mapStyle?: SetStyleConfig;
  markerData?: Array<MarkerData>;
  spotlightedNodeId?: string | null;
  onHover?: EventHandler;
  onTap?: EventHandler;

  mapViewApiRef?: Ref<MapViewApi>;
};

export type MapViewApi = {
  fitBuildingNodes: (...args: Parameters<MapiqMap["fitBuildingNodes"]>) => void;
};

const NO_MAP_STYLE: SetStyleConfig = { preset: "base" };
const NO_MAP_STATE: MapState = {};
const NO_MARKER_DATA: Array<MarkerData> = [];
const NO_NODE_IDS: Array<string> = [];
const DEFAULT_VIEWPORT: AbsoluteViewportRestriction = {
  paddingBottomPixels: 0,
  paddingLeftPixels: 0,
  paddingRightPixels: 0,
  paddingTopPixels: 0,
};

export const MapView = ({
  mapData,
  interactive = true,
  resolveMapiqMap,

  onHover,
  onTap,

  mapStyle = NO_MAP_STYLE,
  mapState = NO_MAP_STATE,
  markerData = NO_MARKER_DATA,
  spotlightedNodeId = null,

  defaultNodeIdsInView = NO_NODE_IDS,
  defaultViewportRestriction = DEFAULT_VIEWPORT,

  mapViewApiRef,
}: MapViewProps) => {
  const mapRef = useRef<MapiqMap>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Store snapshots of the default___ props so we can be sure changing them after mount doesn't impact the component
  const [defaultProps] = useState(() => ({
    nodeIdsInView: [...defaultNodeIdsInView],
    viewportRestriction: { ...defaultViewportRestriction },
  }));

  // Keep event listeners in a ref so we don't have to remove/add event listeners continously
  const onTapRef = useRef(onTap);
  const onHoverRef = useRef(onHover);

  // Construct a function that applies the initial state with whatever the prop state is at that time
  const applyInitialState = useEffectEvent((mapInstance: MapiqMap) => {
    mapInstance.setMarkerData(markerData);
    mapInstance.setStyle(mapStyle);
    mapInstance.setState(mapState);
  });

  // Construct a mapiq map widget and ensure its initial state matches what is defined in props
  useLayoutEffect(
    function createMapiqMap() {
      const container = containerRef.current;
      assertNotNull(container);

      // Call whatever is the current onTap handler at time of event
      const tapHandler = (e: EventTarget) => {
        onTapRef.current?.(e);
      };
      // Call whatever is the current onHover handler at time of event
      const hoverHandler = (e: EventTarget) => {
        onHoverRef.current?.(e);
      };

      // Create a new map
      const mapInstance = resolveMapiqMap({
        mapData: mapData.type === "2d" ? { type: "2d", map: mapData.map } : { type: "3d", map: mapData.map },
        viewportRestriction: defaultProps.viewportRestriction,
        disablePointerEvents: !interactive,
        container,
      });

      // Attach listeners
      mapInstance.addEventListener("tap", tapHandler);
      mapInstance.addEventListener("hover", hoverHandler);

      // Apply default view (later to be controlled via imperative handle)
      mapInstance.fitBuildingNodes({
        nodeIds: defaultProps.nodeIdsInView,
        viewportRestriction: defaultProps.viewportRestriction,
        animated: false,
        abortIfAlreadyInView: false,
      });

      // Apply initial state
      applyInitialState(mapInstance);

      // Expose the instance to the component
      mapRef.current = mapInstance;

      return function cleanupMap() {
        mapInstance.removeEventListener("tap", tapHandler);
        mapInstance.removeEventListener("hover", hoverHandler);
        mapInstance.dispose();
        container.replaceChildren();
        mapRef.current = null;
      };
    },
    [mapData.type, mapData.map, interactive, resolveMapiqMap, defaultProps],
  );

  // Subscribe state updates
  useEffect(
    function updateSpotlight() {
      mapRef.current?.setSpotlight(spotlightedNodeId ?? null);
    },
    [spotlightedNodeId],
  );

  useEffect(
    function updateMarkers() {
      // Note: Might be worth early-exiting when we see markerData ≈≈ prevMarkerData... (updates to markers are expensive)
      //       The reason I haven't implemented this:
      //         If the consumer component correcly uses `useMemo` or react compiler, it's unneeded overhead...
      //         If the map renderer implements this check, it's duplicate work...
      mapRef.current?.setMarkerData(markerData);
    },
    [markerData],
  );

  useEffect(
    function updateStyle() {
      mapRef.current?.setStyle(mapStyle);
    },
    [mapStyle],
  );

  useEffect(
    function updateState() {
      mapRef.current?.setState(mapState);
    },
    [mapState],
  );

  useEffect(
    function updateOnHoverRef() {
      onHoverRef.current = onHover;
    },
    [onHover],
  );

  useEffect(
    function updateOnTapRef() {
      onTapRef.current = onTap;
    },
    [onTap],
  );

  // Expose API for zooming
  useImperativeHandle(
    mapViewApiRef,
    () => {
      return {
        fitBuildingNodes: opts => {
          const mapInstance = mapRef.current;
          assertNotNull(mapInstance);

          mapInstance.fitBuildingNodes(opts);
        },
      };
    },
    [],
  );

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%" }}
    />
  );
};

function assertNotNull<T>(value: T, message = "Expected value to be non-null"): asserts value is NonNullable<T> {
  if (value == null) {
    throw new Error(message);
  }
}
